import React, { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, ShoppingBag, X } from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useCart } from "@/context/CartContext";

const navItems = [
  { label: "Home", to: "/" },
  { label: "Shop", to: "/shop" },
  { label: "Our Story", to: "/our-story" },
  { label: "Impact", to: "/farmers-impact" },
  { label: "Natural Farming", to: "/natural-farming" },
  { label: "Contact", to: "/contact" },
];

export function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { count } = useCart();
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 18);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      animate={{ backgroundColor: scrolled ? "rgba(250,250,248,0.94)" : "rgba(250,250,248,0.78)", boxShadow: scrolled ? "0 14px 40px rgba(44,44,42,0.06)" : "0 0 0 rgba(0,0,0,0)" }}
      transition={{ duration: 0.28 }}
      className="fixed top-0 z-50 w-full border-b border-[#EAEAE6]/70 backdrop-blur-xl"
      data-testid="site-header"
    >
      <div className={`mx-auto flex max-w-7xl items-center justify-between px-5 transition-all duration-300 md:px-10 ${scrolled ? "h-16" : "h-20"}`}>
        <Link data-testid="brand-home-link" to="/" className="group flex items-center">
          <img
            data-testid="brand-logo-image"
            src="/images/brand/tatva-hills-logo-transparent.png"
            alt="Tatva Hills Farm Fresh logo"
            className="h-9 w-auto object-contain transition-transform duration-300 group-hover:scale-[1.02] md:h-10"
          />
        </Link>

        <nav className="hidden items-center gap-6 lg:flex" data-testid="desktop-navigation">
          {navItems.map((item) => (
            <NavLink
              data-testid={`nav-link-${item.label.toLowerCase().replaceAll(" ", "-")}`}
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `nav-underline relative text-sm transition-colors duration-300 ${isActive ? "text-[#2A4B3C] is-active" : "text-[#6B6B66] hover:text-[#2A4B3C]"}`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            data-testid="header-cart-link"
            to="/cart"
            className="relative inline-flex h-11 w-11 items-center justify-center rounded-full border border-[#D5D5D0] bg-white text-[#2A4B3C] transition-transform duration-300 hover:-translate-y-0.5 hover:shadow-[0_10px_24px_rgba(42,75,60,0.12)]"
          >
            <ShoppingBag className="h-5 w-5" />
            <motion.span key={count} initial={reduceMotion ? false : { scale: 0.65 }} animate={reduceMotion ? {} : { scale: 1 }} data-testid="header-cart-count" className="absolute -right-1 -top-1 grid h-5 min-w-5 place-items-center rounded-full bg-[#C84B31] px-1 text-[11px] font-semibold text-white">
              {count}
            </motion.span>
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            type="button"
            onClick={() => setOpen((value) => !value)}
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-[#D5D5D0] bg-white text-[#2A4B3C] lg:hidden"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
      {open && (
        <motion.div initial={reduceMotion ? false : { opacity: 0, y: -10 }} animate={reduceMotion ? {} : { opacity: 1, y: 0 }} exit={reduceMotion ? {} : { opacity: 0, y: -10 }} transition={{ duration: 0.24 }} className="border-t border-[#EAEAE6] bg-[#FAFAF8] px-5 py-5 lg:hidden" data-testid="mobile-navigation">
          <div className="flex flex-col gap-4">
            {navItems.map((item, index) => (
              <motion.div key={item.to} initial={reduceMotion ? false : { opacity: 0, x: -12 }} animate={reduceMotion ? {} : { opacity: 1, x: 0 }} transition={{ delay: index * 0.04 }}>
              <NavLink
                data-testid={`mobile-nav-link-${item.label.toLowerCase().replaceAll(" ", "-")}`}
                to={item.to}
                onClick={() => setOpen(false)}
                className="text-base text-[#2C2C2A]"
              >
                {item.label}
              </NavLink>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}
      </AnimatePresence>
    </motion.header>
  );
}